import type { RoomType } from './firestore';

// ============================================
// ONBOARDING WIZARD TYPES
// ============================================

export type OnboardingStep =
  | 'company_basics'
  | 'widget_config'
  | 'lead_capture'
  | 'summary';

export const ONBOARDING_STEPS: OnboardingStep[] = [
  'company_basics',
  'widget_config',
  'lead_capture',
  'summary',
];

// UI-009 FIX: Updated labels so first word is meaningful when displayed in progress dots
export const STEP_TITLES: Record<OnboardingStep, string> = {
  company_basics: 'Company Info',
  widget_config: 'Widget Settings',
  lead_capture: 'Contact Info',
  summary: 'Complete Setup',
};

export const STEP_DESCRIPTIONS: Record<OnboardingStep, string> = {
  company_basics: 'Tell us about your business',
  widget_config: 'Configure how your widget works',
  lead_capture: 'Set up how customers reach you',
  summary: 'Review your setup and get started',
};

// Steps that can be skipped (use defaults)
export const SKIPPABLE_STEPS: OnboardingStep[] = ['widget_config', 'lead_capture'];

// ============================================
// WIZARD STATE
// ============================================

export interface OnboardingWizardData {
  // Step 1: Company Basics (required)
  companyName: string;
  businessPhone: string;
  primaryColor: string;

  // Step 2: Widget Config (skippable)
  showQuote: boolean;
  requireAddress: boolean;
  requireTimeline: boolean;

  // Step 3: Lead Capture (skippable)
  ctaBookCallUrl: string;
  ctaGetQuoteUrl: string;
}

export interface OnboardingWizardState {
  currentStep: OnboardingStep;
  data: OnboardingWizardData;
  completedSteps: OnboardingStep[];
  isSubmitting: boolean;
}

// ============================================
// DEFAULT VALUES
// ============================================

// FR-7: Smart defaults for simplified onboarding
// CTA text default: 'Get Your Free Design' (handled in widget)
export const DEFAULT_WIZARD_DATA: OnboardingWizardData = {
  companyName: '',
  businessPhone: '',
  primaryColor: '#2563eb',
  showQuote: true,           // Smart default: show quotes
  requireAddress: false,     // Smart default: minimal friction
  requireTimeline: false,    // Smart default: minimal friction
  ctaBookCallUrl: '',
  ctaGetQuoteUrl: '',
};

export const INITIAL_WIZARD_STATE: OnboardingWizardState = {
  currentStep: 'company_basics',
  data: DEFAULT_WIZARD_DATA,
  completedSteps: [],
  isSubmitting: false,
};

// ============================================
// WIZARD ACTIONS
// ============================================

export type OnboardingWizardAction =
  | { type: 'NEXT_STEP' }
  | { type: 'PREV_STEP' }
  | { type: 'GO_TO_STEP'; step: OnboardingStep }
  | { type: 'UPDATE_DATA'; data: Partial<OnboardingWizardData> }
  | { type: 'SKIP_STEP' }
  | { type: 'SET_SUBMITTING'; isSubmitting: boolean }
  | { type: 'RESET' };
